import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Button, Link, Typography } from '@mui/material';
import { IntegrationBannerProps } from '../../../../../../../../features/integrations/types';
import { useAppDispatch, useAppSelector } from '../../../../../../../../hooks/hooks';
import * as zoho from '../../../../../../../../features/integrations/zoho';
import Card from '../../../../../card';
import styles from '../../../../../card/card.module.scss';
import { Lx } from '../../../../../../../../i18n/consts';

enum IntegrationState {
    Active = 'active',
    Awaiting = 'awaiting',
    Disabled = 'disabled'
}

const ZohoIntegrationBanner = (props: IntegrationBannerProps) => {
    const intl = useIntl();
    const integrationData = useAppSelector(store => store.integrationZoho);
    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState(false);
    const { endpoints } = props.integration;
    const connectLabel = intl.formatMessage({ id: Lx.Integration.CONNECT });
    const configureLabel = intl.formatMessage({ id: Lx.Integration.CONFIGURE });
    const disconnectLabel = intl.formatMessage({ id: Lx.Integration.DISCONNECT });
    const canManage = props.canManage ?? true;

    const handleConnectClick = () => {
        props.onConnectClick(props.integration);
    };

    const handleConfigureClick = () => {
        props.onConfigureClick(props.integration);
    };

    const handleDisconnectClick = () => {
        props.onDisconnectClick(props.integration);
    };

    const getIntegrationState = (): IntegrationState => {
        return props.integration.enabled && integrationData && integrationData.enabled
            ? IntegrationState.Active
            : props.integration.enabled && integrationData && !integrationData.enabled
                ? IntegrationState.Awaiting
                : IntegrationState.Disabled;
    };

    const renderStatusText = (state: IntegrationState) => {
        switch (state) {
            case IntegrationState.Active:
                return intl.formatMessage({ id: Lx.General.ACTIVE });
            case IntegrationState.Awaiting:
                return intl.formatMessage({ id: Lx.Integration.WAITING_CONFIGURATION });
            default:
                return intl.formatMessage({ id: Lx.Integration.NOT_CONNECTED });
        }
    };

    const renderCard = (state: IntegrationState) => {
        return (
            <Card
                title="Zoho"
                statusText={renderStatusText(state)}
                isStatusActive={state === IntegrationState.Active}
                actions={
                    <div className={styles.actionsColumn}>
                        <Button
                            className={`${styles.actionButton} ${styles.integrationActionButton} ${state === IntegrationState.Active ? styles.actionButtonActive : styles.actionButtonInactive}`}
                            onClick={state === IntegrationState.Disabled ? handleConnectClick : handleConfigureClick}
                            disabled={!canManage}>
                            {state === IntegrationState.Disabled ? connectLabel : configureLabel}
                        </Button>
                        {state !== IntegrationState.Disabled ? (
                            <Button
                                className={`${styles.actionButton} ${styles.integrationActionButton} ${styles.actionButtonActive}`}
                                onClick={handleDisconnectClick}
                                disabled={!canManage}>
                                {disconnectLabel}
                            </Button>
                        ) : null}
                    </div>
                }>
                <div className={styles.integrationDescriptionWrapper}>
                    <Typography
                        component="span"
                        className={styles.integrationDescriptionText}>
                        {intl.formatMessage({ id: Lx.Integration.Zoho.DESC })}:&nbsp;
                        <Link href={intl.formatMessage({ id: Lx.Integration.Zoho.LINK })} target='_blank' rel='noreferrer'>{intl.formatMessage({ id: Lx.Integration.Zoho.LINK })}</Link>
                    </Typography>
                </div>
            </Card>
        );
    };

    useEffect(() => {
        setLoading(true);
        dispatch(zoho.get({ endpoint: endpoints.get })).then(() => {
            setLoading(false);
        });
    }, [dispatch, endpoints.get]);

    return <>{!loading && integrationData ? renderCard(getIntegrationState()) : null}</>;
};

export default ZohoIntegrationBanner;
